import React, { useState, useRef, useEffect, useCallback } from 'react';

const TOOLS = [
  { id: 'arrow', label: '➡ Arrow', cursor: 'crosshair' },
  { id: 'rect', label: '▭ Rectangle', cursor: 'crosshair' },
  { id: 'blur', label: '▦ Blur', cursor: 'crosshair' },
  { id: 'text', label: 'T Text', cursor: 'text' },
  { id: 'freehand', label: '✎ Draw', cursor: 'crosshair' },
];

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff', '#000000'];

export default function ImageEditor({ imageUrl, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const imgRef = useRef(null);
  const [tool, setTool] = useState('arrow');
  const [color, setColor] = useState('#ef4444');
  const [lineWidth, setLineWidth] = useState(3);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [textInput, setTextInput] = useState(null);
  const [freehandPoints, setFreehandPoints] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const getCanvasSize = useCallback(() => {
    if (!imgRef.current) return { w: 800, h: 600 };
    const maxW = Math.min(window.innerWidth * 0.85, 1200);
    const maxH = window.innerHeight * 0.7;
    const iw = imgRef.current.naturalWidth;
    const ih = imgRef.current.naturalHeight;
    const scale = Math.min(maxW / iw, maxH / ih, 1);
    return { w: Math.round(iw * scale), h: Math.round(ih * scale), scale, iw, ih };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const { w, h } = getCanvasSize();
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgRef.current, 0, 0, w, h);

    annotations.forEach(a => drawAnnotation(ctx, a, w, h));
  }, [annotations, getCanvasSize]);

  useEffect(() => { if (loaded) redraw(); }, [loaded, redraw]);

  const drawAnnotation = (ctx, a, w, h) => {
    ctx.save();
    ctx.strokeStyle = a.color;
    ctx.fillStyle = a.color;
    ctx.lineWidth = a.lineWidth || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (a.type) {
      case 'arrow': {
        const dx = a.x2 - a.x1, dy = a.y2 - a.y1;
        const angle = Math.atan2(dy, dx);
        const headLen = 15;
        ctx.beginPath();
        ctx.moveTo(a.x1, a.y1);
        ctx.lineTo(a.x2, a.y2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(a.x2, a.y2);
        ctx.lineTo(a.x2 - headLen * Math.cos(angle - 0.4), a.y2 - headLen * Math.sin(angle - 0.4));
        ctx.lineTo(a.x2 - headLen * Math.cos(angle + 0.4), a.y2 - headLen * Math.sin(angle + 0.4));
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'rect':
        ctx.strokeRect(a.x, a.y, a.w, a.h);
        break;
      case 'blur': {
        const size = getCanvasSize();
        const scaleX = imgRef.current.naturalWidth / size.w;
        const scaleY = imgRef.current.naturalHeight / size.h;
        const sx = Math.max(0, Math.round(a.x * scaleX));
        const sy = Math.max(0, Math.round(a.y * scaleY));
        const sw = Math.min(Math.round(a.w * scaleX), imgRef.current.naturalWidth - sx);
        const sh = Math.min(Math.round(a.h * scaleY), imgRef.current.naturalHeight - sy);
        if (sw > 0 && sh > 0) {
          const pixelSize = 10;
          const tmpCanvas = document.createElement('canvas');
          tmpCanvas.width = sw;
          tmpCanvas.height = sh;
          const tmpCtx = tmpCanvas.getContext('2d');
          tmpCtx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, sw, sh);
          tmpCtx.imageSmoothingEnabled = false;
          const smallW = Math.max(1, Math.round(sw / pixelSize));
          const smallH = Math.max(1, Math.round(sh / pixelSize));
          tmpCtx.drawImage(tmpCanvas, 0, 0, sw, sh, 0, 0, smallW, smallH);
          tmpCtx.drawImage(tmpCanvas, 0, 0, smallW, smallH, 0, 0, sw, sh);
          ctx.drawImage(tmpCanvas, 0, 0, sw, sh, a.x, a.y, a.w, a.h);
        }
        break;
      }
      case 'text':
        ctx.font = `bold ${a.fontSize || 16}px Arial`;
        ctx.fillText(a.text, a.x, a.y);
        break;
      case 'freehand':
        if (a.points.length < 2) break;
        ctx.beginPath();
        ctx.moveTo(a.points[0].x, a.points[0].y);
        for (let i = 1; i < a.points.length; i++) ctx.lineTo(a.points[i].x, a.points[i].y);
        ctx.stroke();
        break;
      default: break;
    }
    ctx.restore();
  };

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onMouseDown = (e) => {
    const pos = getPos(e);
    if (tool === 'text') {
      setTextInput({ x: pos.x, y: pos.y });
      return;
    }
    setStartPos(pos);
    setDrawing(true);
    if (tool === 'freehand') setFreehandPoints([pos]);
  };

  const onMouseMove = (e) => {
    if (!drawing || !startPos) return;
    const pos = getPos(e);

    if (tool === 'freehand') {
      setFreehandPoints(prev => [...prev, pos]);
    }

    // Live preview on overlay canvas
    const overlay = overlayRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === 'arrow') {
      const dx = pos.x - startPos.x, dy = pos.y - startPos.y;
      const angle = Math.atan2(dy, dx);
      const headLen = 15;
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.lineTo(pos.x - headLen * Math.cos(angle - 0.4), pos.y - headLen * Math.sin(angle - 0.4));
      ctx.lineTo(pos.x - headLen * Math.cos(angle + 0.4), pos.y - headLen * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();
    } else if (tool === 'rect' || tool === 'blur') {
      const x = Math.min(startPos.x, pos.x), y = Math.min(startPos.y, pos.y);
      const w = Math.abs(pos.x - startPos.x), h = Math.abs(pos.y - startPos.y);
      ctx.setLineDash(tool === 'blur' ? [6, 3] : []);
      ctx.strokeRect(x, y, w, h);
    } else if (tool === 'freehand') {
      const pts = [...freehandPoints, pos];
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
  };

  const onMouseUp = (e) => {
    if (!drawing || !startPos) return;
    setDrawing(false);
    const pos = getPos(e);

    let annotation;
    if (tool === 'arrow') {
      annotation = { type: 'arrow', x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y, color, lineWidth };
    } else if (tool === 'rect') {
      annotation = { type: 'rect', x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y), w: Math.abs(pos.x - startPos.x), h: Math.abs(pos.y - startPos.y), color, lineWidth };
    } else if (tool === 'blur') {
      annotation = { type: 'blur', x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y), w: Math.abs(pos.x - startPos.x), h: Math.abs(pos.y - startPos.y) };
    } else if (tool === 'freehand') {
      annotation = { type: 'freehand', points: [...freehandPoints, pos], color, lineWidth };
      setFreehandPoints([]);
    }

    if (annotation) {
      setAnnotations(prev => [...prev, annotation]);
    }

    // Clear overlay
    const overlay = overlayRef.current;
    if (overlay) overlay.getContext('2d').clearRect(0, 0, overlay.width, overlay.height);
    setStartPos(null);
  };

  const addText = (text) => {
    if (!text || !textInput) return;
    setAnnotations(prev => [...prev, { type: 'text', x: textInput.x, y: textInput.y, text, color, fontSize: lineWidth * 5 + 6 }]);
    setTextInput(null);
  };

  const undo = () => setAnnotations(prev => prev.slice(0, -1));

  const handleSave = () => {
    // Render at full resolution
    const fullCanvas = document.createElement('canvas');
    fullCanvas.width = imgRef.current.naturalWidth;
    fullCanvas.height = imgRef.current.naturalHeight;
    const ctx = fullCanvas.getContext('2d');
    ctx.drawImage(imgRef.current, 0, 0);

    const { w, h } = getCanvasSize();
    const scaleX = imgRef.current.naturalWidth / w;
    const scaleY = imgRef.current.naturalHeight / h;

    annotations.forEach(a => {
      const scaled = scaleAnnotation(a, scaleX, scaleY);
      drawAnnotation(ctx, scaled, fullCanvas.width, fullCanvas.height);
    });

    fullCanvas.toBlob(blob => { if (blob) onSave(blob); }, 'image/png');
  };

  const scaleAnnotation = (a, sx, sy) => {
    switch (a.type) {
      case 'arrow': return { ...a, x1: a.x1 * sx, y1: a.y1 * sy, x2: a.x2 * sx, y2: a.y2 * sy, lineWidth: a.lineWidth * Math.max(sx, sy) };
      case 'rect': return { ...a, x: a.x * sx, y: a.y * sy, w: a.w * sx, h: a.h * sy, lineWidth: a.lineWidth * Math.max(sx, sy) };
      case 'blur': return { ...a, x: a.x * sx, y: a.y * sy, w: a.w * sx, h: a.h * sy };
      case 'text': return { ...a, x: a.x * sx, y: a.y * sy, fontSize: (a.fontSize || 16) * Math.max(sx, sy) };
      case 'freehand': return { ...a, points: a.points.map(p => ({ x: p.x * sx, y: p.y * sy })), lineWidth: a.lineWidth * Math.max(sx, sy) };
      default: return a;
    }
  };

  const { w: cw, h: ch } = loaded ? getCanvasSize() : { w: 800, h: 600 };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', background: '#1f2937', borderRadius: '0 0 12px 12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {TOOLS.map(t => (
          <button key={t.id} onClick={() => setTool(t.id)}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none',
              background: tool === t.id ? '#3b82f6' : '#374151', color: '#fff', cursor: 'pointer'
            }}>{t.label}</button>
        ))}
        <div style={{ width: 1, height: 24, background: '#4b5563', margin: '0 4px' }} />
        {COLORS.map(c => (
          <button key={c} onClick={() => setColor(c)}
            style={{
              width: 22, height: 22, borderRadius: '50%', border: color === c ? '2px solid #fff' : '2px solid transparent',
              background: c, cursor: 'pointer', padding: 0
            }} />
        ))}
        <div style={{ width: 1, height: 24, background: '#4b5563', margin: '0 4px' }} />
        <label style={{ color: '#9ca3af', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
          Size
          <input type="range" min="1" max="8" value={lineWidth} onChange={e => setLineWidth(Number(e.target.value))}
            style={{ width: 60 }} />
        </label>
        <div style={{ width: 1, height: 24, background: '#4b5563', margin: '0 4px' }} />
        <button onClick={undo} disabled={annotations.length === 0}
          style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: 'none', background: '#374151', color: annotations.length ? '#fff' : '#6b7280', cursor: 'pointer' }}>
          Undo
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={handleSave}
          style={{ padding: '6px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', background: '#22c55e', color: '#fff', cursor: 'pointer' }}>
          Save
        </button>
        <button onClick={onCancel}
          style={{ padding: '6px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #6b7280', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
          Cancel
        </button>
      </div>

      {/* Canvas area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ position: 'relative' }}>
          <canvas ref={canvasRef} width={cw} height={ch}
            style={{ display: 'block', borderRadius: 4, cursor: TOOLS.find(t => t.id === tool)?.cursor || 'crosshair' }} />
          <canvas ref={overlayRef} width={cw} height={ch}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
            style={{ position: 'absolute', top: 0, left: 0, cursor: TOOLS.find(t => t.id === tool)?.cursor || 'crosshair' }} />
          {textInput && (
            <input autoFocus placeholder="Type text..."
              style={{
                position: 'absolute', left: textInput.x, top: textInput.y - 20,
                background: 'rgba(0,0,0,0.7)', color: '#fff', border: '1px solid #3b82f6',
                borderRadius: 4, padding: '4px 8px', fontSize: 14, outline: 'none', minWidth: 120
              }}
              onKeyDown={e => { if (e.key === 'Enter') addText(e.target.value); if (e.key === 'Escape') setTextInput(null); }}
              onBlur={e => { if (e.target.value) addText(e.target.value); else setTextInput(null); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
